/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import clamp from 'cio/utils/clamp';

interface CodeSnippetGroupArgs {
  activeSnippetName: string;
  updateActiveSnippet: (name: string) => unknown;
  snippets: {
    name: string;
    content: string;
  }[];
  highlightedLinesStart: number;
  highlightedLinesCount: number;
  onCopy: () => unknown;
}

export default class CodeSnippetGroup extends Component<CodeSnippetGroupArgs> {
  get snippets() {
    return this.args.snippets.map((snippet) => {
      let lines = this.lines(snippet.content);

      return {
        ...snippet,
        lineNumbers: lines.join('\n'),
        lineCount: lines.length,
      };
    });
  }

  get activeSnippet() {
    return this.snippets.find((snippet) => snippet.name === this.args.activeSnippetName);
  }

  get hasHighlightedLines() {
    return this.args.highlightedLinesStart !== undefined && this.args.highlightedLinesCount > 0;
  }

  @action
  highlightLines(element: HTMLElement) {
    if (!this.hasHighlightedLines) {
      return;
    }

    // Keep the highlight within the bounds of the snippet
    let lineCount = this.activeSnippet?.lineCount ?? 0;

    // Highlight must start between 0 and the total number of lines
    let start = clamp(this.args.highlightedLinesStart, lineCount, 0);
    element.style.setProperty('--highlight-start', String(start));

    // Highlight must also end between the start point and the total number of
    // lines that _remain_ For example: in a 20 line snippet, if the highlight
    // starts at line 5, it can be a maximum of 15 lines.
    element.style.setProperty(
      '--highlight-lines',
      String(clamp(this.args.highlightedLinesCount, lineCount - start, 0)),
    );
  }

  @action
  visitTab(name: string) {
    if (name === this.args.activeSnippetName) {
      return;
    }

    this.args.updateActiveSnippet(name);
  }

  @action
  onCopy() {
    if (this.args.onCopy) {
      this.args.onCopy();
    }
  }

  // Create newline separated line numbers for the snippet
  private lines(snippet: string) {
    return snippet.split('\n').map((_, i) => `${i + 1}`);
  }
}
